import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  Stack,
  Link,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Link as LinkIcon,
  Public as PublicIcon,
  CheckCircle as CheckCircleIcon,
  CircleOutlined as CircleOutlinedIcon,
  Api as ApiIcon,
  CalendarToday as CalendarIcon,
  Translate as TranslateIcon,
  ColorLens as ColorLensIcon,
  Receipt as ReceiptIcon,
  Category as CategoryIcon,
  Badge as BadgeIcon,
  Add as AddIcon,
  InfoOutlined as InfoIcon,
  PersonAdd as PersonAddIcon,
  HourglassEmpty as HourglassEmptyIcon, // Added for the stepper
} from "@mui/icons-material";

// ── Design System Imports ──────────────────────────────────────────────────────
import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
  formConstants,
  inputColor,
} from "../../cssUtils/constants";

// ── Shared Components ──────────────────────────────────────────────────────────
import ButtonOne   from "../../common/normal-button";
import ButtonTwo   from "../../common/dashboard-button";
import BackButton  from "../../common/back-button";
import AppChip     from "../../common/app-chip";
import { formatInTenantTz } from "../../utils/dateUtils";

import AddTesterModal from "./AddTesterModal";

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens
// ─────────────────────────────────────────────────────────────────────────────
const CARD_SX = {
  backgroundColor: ColorsConstants.primaryWhite,
  border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
  borderRadius: "12px",
};

const INFO_ROW_SX = {
  display: "flex",
  alignItems: "flex-start",
  py: 1.75,
  borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
  "&:last-child": { borderBottom: "none" },
};

const LABEL_SX = {
  ...typography.bodySmall,
  color: grayFontColor,
  flex: "0 0 160px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

const VALUE_SX = {
  ...typography.bodyLarge,
  fontWeight: 500,
  color: ColorsConstants.primaryBlue100,
  flex: 1,
  wordBreak: "break-word",
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper / label functions 
// ─────────────────────────────────────────────────────────────────────────────
const getRegionLabel = (region) => {
  switch (region) {
    case "INDIA": return "India";
    case "ROW":   return "Rest of World";
    default:      return region;
  }
};

const getMessageTypeLabel = (type) => {
  switch (type) {
    case "PROMOTIONAL":   return "Promotional";
    case "OTP":           return "OTP";
    case "MULTI_USE":     return "Multi-Use";
    case "TRANSACTIONAL": return "Transactional";
    default:              return type;
  }
};

const getBillingCategoryLabel = (category) => {
  switch (category) {
    case "CONVERSATIONAL":     return "Conversational";
    case "NON_CONVERSATIONAL": return "Non-Conversational";
    default:                   return category;
  }
};

const getApiLabel = (api) => {
  switch (api) {
    case "GSMA":   return "GSMA API";
    case "GOOGLE": return "Google RBM API";
    default:       return api;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components 
// ─────────────────────────────────────────────────────────────────────────────

const InfoTooltip = ({ title }) => (
  <Tooltip title={title} arrow>
    <InfoIcon sx={{ fontSize: 13, color: grayFontColor, cursor: "help" }} />
  </Tooltip>
);

function InfoRow({ label, value, icon: Icon, hint }) {
  return (
    <Box sx={INFO_ROW_SX}>
      <Box sx={LABEL_SX}>
        {Icon && <Icon sx={{ color: grayFontColor, fontSize: 16 }} />}
        <Typography sx={LABEL_SX}>
          {label}
          {hint && <InfoTooltip title={hint} />}
        </Typography>
      </Box>
      <Box sx={VALUE_SX}>{value ?? "—"}</Box>
    </Box>
  );
}

function ColorRow({ label, color }) {
  return (
    <Box sx={INFO_ROW_SX}>
      <Box sx={LABEL_SX}>
        <ColorLensIcon sx={{ color: grayFontColor, fontSize: 16 }} />
        <Typography sx={LABEL_SX}>{label}</Typography>
      </Box>
      <Box sx={VALUE_SX}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 22, height: 22, borderRadius: "4px", backgroundColor: color, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }} />
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>{color}</Typography>
        </Stack>
      </Box>
    </Box>
  );
}

function LanguagesRow({ languages }) {
  return (
    <Box sx={INFO_ROW_SX}>
      <Box sx={LABEL_SX}>
        <TranslateIcon sx={{ color: grayFontColor, fontSize: 16 }} />
        <Typography sx={LABEL_SX}>Languages</Typography>
      </Box>
      <Box sx={VALUE_SX}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {languages?.split(",").map((lang, idx) => (
            <AppChip
              key={idx}
              label={lang.trim().toUpperCase()}
              variant="outlined"
              sx={{
                borderRadius: "10px",
                borderColor: borderColors.cardBorderPrimaryGrayColor,
                backgroundColor: ColorsConstants.primaryBlue60,
                color: ColorsConstants.primaryBlue100,
              }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function StatusBadge({ status }) {
  const colorMap = {
    APPROVED:             { bg: "#E8F5E9", color: "#2E7D32",  label: "Approved" },
    SUBMITTED:            { bg: "#FFF3E0", color: "#ED6C02",  label: "Submitted" },
    PENDING_VERIFICATION: { bg: "#E3F2FD", color: ColorsConstants.primaryPurple100, label: "Pending Verification" },
    DRAFT:                { bg: ColorsConstants.primaryBlue60, color: grayFontColor, label: "Draft" },
    SUCCESS:              { bg: "#E8F5E9", color: "#2E7D32",  label: "Success" }, // Added based on your requirements
    LAUNCH:               { bg: ColorsConstants.primaryPurple100 + "20", color: ColorsConstants.primaryPurple100,  label: "Live" } // Added based on your requirements
  };
  const style = colorMap[status] || colorMap.DRAFT;
  return (
    <Chip
      label={style.label}
      size="medium"
      sx={{
        fontWeight: 700,
        ...typography.bodySmall,
        borderRadius: "20px",
        px: 0.5,
        backgroundColor: style.bg,
        color: style.color,
      }}
    />
  );
}

function ConfigStatusItem({ label, completed }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
      {completed ? (
        <CheckCircleIcon sx={{ color: formConstants.greenButton, fontSize: 20 }} />
      ) : (
        <CircleOutlinedIcon sx={{ color: grayFontColor, fontSize: 20 }} />
      )}
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
          {label}
        </Typography>
        <Typography sx={{ ...typography.bodySmall, color: completed ? formConstants.greenButton : grayFontColor }}>
          {completed ? "Complete" : "Pending"}
        </Typography>
      </Box>
    </Box>
  );
}

function ContactCard({ icon: Icon, title, hasData, emptyIcon: EmptyIcon, children }) {
  return (
    <Box sx={{ ...CARD_SX, height: "100%" }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: ColorsConstants.primaryBlue60, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon sx={{ color: ColorsConstants.primaryBlue100, fontSize: 18 }} />
          </Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>
            {title}
          </Typography>
        </Stack>
        {hasData ? (
          <Stack spacing={1.5}>{children}</Stack>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 4 }}>
            <EmptyIcon sx={{ fontSize: 42, color: ColorsConstants.inputFieldGrey, mb: 1.5 }} />
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              No {title.toLowerCase()} configured
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function ContactField({ label, children }) {
  return (
    <Box>
      <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 0.5 }}>{label}</Typography>
      {children}
    </Box>
  );
}

function SectionCard({ children, sx = {} }) {
  return <Box sx={{ ...CARD_SX, p: 3, height: "100%", ...sx }}>{children}</Box>;
}

function CardTitle({ children }) {
  return (
    <Typography sx={{ ...typography.h2, color: ColorsConstants.primaryBlue100, mb: 2 }}>
      {children}
    </Typography>
  );
}

function MonoBox({ children }) {
  return (
    <Box sx={{ backgroundColor: ColorsConstants.primaryBlue60, px: 1.5, py: 1, borderRadius: "8px", display: "inline-block", wordBreak: "break-all" }}>
      <Typography sx={{ ...typography.bodySmall, fontFamily: "monospace", color: ColorsConstants.primaryBlue100 }}>
        {children || "Not assigned"}
      </Typography>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// 🟢 NEW: Custom Bot Progress Stepper (Built exactly to match your screenshot)
// ─────────────────────────────────────────────────────────────────────────────
function BotProgressStepper({ status }) {
  // Logic to determine the current active step based on your rules:
  // bot creation == submitted, pending
  // development == approved
  // verification == success
  // launch == launch

  let activeStep = 0; // Default: Bot Creation
  const statusUpper = status?.toUpperCase() || "";

  if (["SUBMITTED", "PENDING_VERIFICATION", "DRAFT"].includes(statusUpper)) {
    activeStep = 0; 
  } else if (statusUpper === "APPROVED") {
    activeStep = 1; 
  } else if (statusUpper === "SUCCESS") {
    activeStep = 2; 
  } else if (statusUpper === "VERIFIED") {
    activeStep = 3; 
  }

  const steps = [
    { label: "Bot Creation", inProcessSub: "In Process", completedSub: "Completed" },
    { label: "Development",  inProcessSub: "In Process", completedSub: "Completed" },
    { label: "Verification", inProcessSub: "In Process", completedSub: "Completed" },
    { label: "Launch",       inProcessSub: "Live",       completedSub: "Completed" }
  ];

  return (
    <Box sx={{ 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center",
      py: 4,
      px: 2,
      backgroundColor: ColorsConstants.primaryWhite,
      border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
      borderRadius: "12px",
      mb: 3
    }}>
      {steps.map((step, index) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;
        const isFuture = index > activeStep;

        return (
          <React.Fragment key={step.label}>
            {/* Step Pill */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  px: 3,
                  py: 1,
                  borderRadius: "20px",
                  // Colors based exactly on the provided screenshot
                  backgroundColor: isCompleted ? "#8BD659" : isActive ? "#FBB408" : "#F5F5F5",
                  color: isCompleted || isActive ? "#FFFFFF" : "#555555",
                  fontWeight: 600,
                  ...typography.bodyMedium,
                  minWidth: "140px",
                }}
              >
                {/* Add hourglass icon only for the active step */}
                {isActive && <HourglassEmptyIcon sx={{ fontSize: 16 }} />}
                {step.label}
              </Box>
              
              {/* Subtext below the pill */}
              <Typography
                sx={{
                  mt: 1,
                  fontSize: "12px",
                  fontStyle: "italic",
                  color: isCompleted || isActive ? "#888888" : "transparent",
                  minHeight: "18px" // Keep spacing even if text is transparent
                }}
              >
                {isCompleted ? step.completedSub : isActive ? step.inProcessSub : ""}
              </Typography>
            </Box>

            {/* Connector Line / Dot */}
            {index < steps.length - 1 && (
              <Box sx={{ display: "flex", alignItems: "center", mx: 1.5, mb: "26px" /* Offset to align with pills, not subtext */ }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: isCompleted ? "#8BD659" : isActive ? "#FBB408" : "#E0E0E0"
                  }}
                />
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function RcsBotDetails({
  bot,
  onBack,
  onViewTemplates,
  onEditBot,
  onSuccess,
  onCreateTemplate,
  onNavigate,
  onVerifyBot,
}) {
  const [isTesterModalOpen, setIsTesterModalOpen] = useState(false);

  const permissionsFromStorage = JSON.parse(
    localStorage.getItem("permissions") || "[]"
  );

  const handleVerificationRedirect = () => {
    console.log("Verification button clicked", { botId: bot.id, botDotgoId: bot.dotgo_bot_id });
    if (onVerifyBot) {
      onVerifyBot();
    }
  };

  return (
    <Box sx={{ backgroundColor: ColorsConstants.primaryBlue60, minHeight: "100vh", fontFamily: "'Lexend', 'Roboto', sans-serif" }}>

      <Box sx={{ backgroundColor: ColorsConstants.primaryWhite, borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, px: 4, py: 1.5 }}>
        <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100 }}>
          RCS Bot Details
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 1280, mx: "auto", px: 3, py: 3 }}>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <BackButton onClick={onBack} />

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>

            {bot.dotgo_bot_id && (
              <ButtonOne
                variant="outlined"
                startIcon={<PersonAddIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => setIsTesterModalOpen(true)}
                sx={{ height: 40, ...typography.bodySmall }}
              >
                Add Tester
              </ButtonOne>
            )}

            {permissionsFromStorage.includes("rcs_template.create") && (
              <ButtonTwo
                startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => onCreateTemplate && onCreateTemplate(bot)}
                sx={{ height: 38, ...typography.bodySmall }}
              >
                Create Template
              </ButtonTwo>
            )}

            {permissionsFromStorage.includes("rcs_bot_create.edit") && (
              <ButtonOne
                disabled
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => onEditBot(bot)}
                sx={{ height: 40, ...typography.bodySmall }}
              >
                Edit Bot
              </ButtonOne>
            )}

            {bot.status === "APPROVED" && bot.status !== "SUBMITTED" && bot.status !== "PENDING_VERIFICATION" && (
              <ButtonOne
                variant="contained"
                startIcon={<CheckCircleIcon sx={{ fontSize: "16px !important" }} />}
                onClick={handleVerificationRedirect}
                sx={{
                  height: 40,
                  ...typography.bodySmall,
                  backgroundColor: ColorsConstants.primaryPurple100,
                  "&:hover": { backgroundColor: ColorsConstants.primaryPurple100, opacity: 0.9 },
                }}
              >
                Submit for Verification
              </ButtonOne>
            )}

            {permissionsFromStorage.includes("rcs_template.view") && (
              <ButtonOne
                variant="contained"
                startIcon={<MessageIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => {
                 onNavigate("view_templates", {
                  activeTab: 1,     // force RCS tab
                  selectedBot: bot, // pass bot
                });
                }}
                sx={{ height: 40, ...typography.bodySmall }}
              >
                View Templates
              </ButtonOne>
            )}
          </Stack>
        </Stack>

        <Box sx={{ ...CARD_SX, mb: 3, overflow: "hidden" }}>
          <Box sx={{ height: 130, backgroundColor: bot.theme_color || ColorsConstants.primaryBlue100, position: "relative", overflow: "hidden" }}>
            {bot.banner_image && (
              <Box component="img" src={bot.banner_image} alt="Banner"
                sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
            <Box sx={{ position: "absolute", top: 14, right: 16 }}>
              <StatusBadge status={bot.status} />
            </Box>
          </Box>

          <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
              <Avatar
                src={bot.bot_logo}
                sx={{
                  width: 80, height: 80,
                  border: `4px solid ${ColorsConstants.primaryWhite}`,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                  mt: { xs: -4, sm: -5 },
                  backgroundColor: ColorsConstants.primaryBlue70,
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryWhite }}>
                  {bot.bot_name?.[0]?.toUpperCase()}
                </Typography>
              </Avatar>

              <Box sx={{ flex: 1, pb: 0.5 }}>
                <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100, mb: 0.25 }}>
                  {bot.bot_name}
                </Typography>
                <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mb: 0.25 }}>
                  {bot.brand_name}
                </Typography>
                <Typography sx={{ ...typography.bodySmall, color: grayFontColor, maxWidth: 580 }}>
                  {bot.short_description}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2} sx={{ pb: 0.5, flexShrink: 0 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>Region</Typography>
                  <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
                    {getRegionLabel(bot.region)}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>Type</Typography>
                  <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
                    {getMessageTypeLabel(bot.message_type)}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>API</Typography>
                  <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
                    {getApiLabel(bot.rcs_api)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* 🟢 NEW: Render the custom stepper right below the banner and above the cards */}
        <BotProgressStepper status={bot.status} />

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <SectionCard>
              <CardTitle>Bot Information</CardTitle>
              <InfoRow label="Bot Name"        value={bot.bot_name}                                icon={BadgeIcon}    hint="The name users see at the top of the message thread" />
              <InfoRow label="Brand Name"       value={bot.brand_name}                              icon={CategoryIcon} hint="Brand associated with this bot" />
              <InfoRow label="Message Type"     value={getMessageTypeLabel(bot.message_type)}       icon={MessageIcon}  hint="Select the message type for your bot" />
              <InfoRow label="Billing Category" value={getBillingCategoryLabel(bot.billing_category)} icon={ReceiptIcon} hint="Select the billing category for your chatbot" />
              <ColorRow label="Theme Color"     color={bot.theme_color} />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard>
              <CardTitle>Configuration</CardTitle>
              <ConfigStatusItem label="Bot Logo"           completed={!!bot.bot_logo} />
              <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
              <ConfigStatusItem label="Banner Image"       completed={!!bot.banner_image} />
              <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
              <ConfigStatusItem label="Webhook"            completed={!!bot.webhook_url} />
              <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
              <ConfigStatusItem label="DotGo Integration"  completed={!!bot.dotgo_bot_id} />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard>
              <CardTitle>Languages &amp; Localisation</CardTitle>
              <LanguagesRow languages={bot.languages_supported} />
              <InfoRow
                label="Created Date"
                value={formatInTenantTz(bot.created_at, "MMMM D, YYYY")}
                icon={CalendarIcon}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard>
              <CardTitle>System Identifiers</CardTitle>
              <InfoRow
                label="DotGo Bot ID"
                value={<MonoBox>{bot.dotgo_bot_id}</MonoBox>}
                icon={ApiIcon}
                hint="Unique identifier for your bot in the DotGo system"
              />
              <InfoRow
                label="Brand Name"
                value={bot.brand_name}
                icon={BadgeIcon}
              />
              <InfoRow
                label="Region"
                value={getRegionLabel(bot.region)}
                icon={PublicIcon}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard>
              <CardTitle>API Configuration</CardTitle>
              <Stack spacing={2.5}>
                <Box>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 0.75 }}>RCS API Provider</Typography>
                  <AppChip
                    label={getApiLabel(bot.rcs_api)}
                    sx={{
                      backgroundColor: ColorsConstants.primaryBlue60,
                      color: ColorsConstants.primaryBlue100,
                      borderRadius: "10px",
                      fontWeight: 600,
                      height: 28,
                    }}
                  />
                </Box>

                <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />

                <Box>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 0.75 }}>Webhook URL</Typography>
                  {bot.webhook_url ? (
                    <Link
                      href={bot.webhook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ ...typography.bodyLarge, fontWeight: 500, color: ColorsConstants.primaryPurple100, textDecoration: "none", wordBreak: "break-all", display: "block", "&:hover": { textDecoration: "underline" } }}
                    >
                      {bot.webhook_url}
                    </Link>
                  ) : (
                    <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>Not configured</Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />

                <Box>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 0.75 }}>DotGo Bot ID</Typography>
                  <MonoBox>{bot.dotgo_bot_id}</MonoBox>
                </Box>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContactCard icon={PhoneIcon} title="Phone Contact" hasData={!!bot.phone_number} emptyIcon={PhoneIcon}>
              <ContactField label="Phone Number">
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
                  {bot.phone_number}
                </Typography>
              </ContactField>
              {bot.phone_label && (
                <ContactField label="Label">
                  <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryBlue100 }}>
                    {bot.phone_label}
                  </Typography>
                </ContactField>
              )}
            </ContactCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContactCard icon={EmailIcon} title="Email Contact" hasData={!!bot.email_id} emptyIcon={EmailIcon}>
              <ContactField label="Email Address">
                <Link
                  href={`mailto:${bot.email_id}`}
                  sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryPurple100, textDecoration: "none", wordBreak: "break-word", display: "block", "&:hover": { textDecoration: "underline" } }}
                >
                  {bot.email_id}
                </Link>
              </ContactField>
              {bot.email_label && (
                <ContactField label="Label">
                  <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryBlue100 }}>
                    {bot.email_label}
                  </Typography>
                </ContactField>
              )}
            </ContactCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContactCard icon={LinkIcon} title="Website" hasData={!!bot.website_url} emptyIcon={LanguageIcon}>
              <ContactField label="URL">
                <Link
                  href={bot.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryPurple100, textDecoration: "none", wordBreak: "break-word", display: "block", "&:hover": { textDecoration: "underline" } }}
                >
                  {bot.website_url}
                </Link>
              </ContactField>
              {bot.website_label && (
                <ContactField label="Label">
                  <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryBlue100 }}>
                    {bot.website_label}
                  </Typography>
                </ContactField>
              )}
            </ContactCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard>
              <CardTitle>Configuration Status</CardTitle>
              <Divider sx={{ mb: 1, borderColor: borderColors.cardBorderPrimaryGrayColor }} />
              <ConfigStatusItem label="Bot Logo Uploaded"        completed={!!bot.bot_logo} />
              <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
              <ConfigStatusItem label="Banner Image Uploaded"    completed={!!bot.banner_image} />
              <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
              <ConfigStatusItem label="Webhook Configured"       completed={!!bot.webhook_url} />
              <Divider sx={{ borderColor: borderColors.cardBorderPrimaryGrayColor }} />
              <ConfigStatusItem label="DotGo Integration Active" completed={!!bot.dotgo_bot_id} />
            </SectionCard>
          </Grid>

        </Grid>
      </Box>

      <AddTesterModal
        open={isTesterModalOpen}
        onClose={() => setIsTesterModalOpen(false)}
        botId={bot.id}
        onSuccess={(data) => {
          onSuccess("Tester invite sent! The user must tap 'Accept' on their device.");
        }}
      />
    </Box>
  );
}